import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { AlertsService } from '../../../services/alerts.service';
import { UserService } from '../../../services/user.service';

/**
 * Top-of-page banner shown on every route until the user acknowledges the
 * data privacy agreement. Cannot be dismissed — only disappears once the
 * agreement is signed.
 */
@Component({
  selector: 'app-privacy-banner',
  imports: [CommonModule, MatIconModule, MatButtonModule, MatSnackBarModule],
  templateUrl: './privacy-banner.html',
  styleUrl: './privacy-banner.css',
})
export class PrivacyBanner implements OnInit, OnDestroy {
  /** Subscription to user updates so the banner reacts to agreement changes. */
  private sub!: Subscription;

  constructor(
    /** Computes whether the privacy agreement is still pending. */
    private alertsService: AlertsService,
    /** Provides the current user and update notifications. */
    private userService: UserService,
    /** Used to navigate to the privacy agreement page. */
    private router: Router,
    /** Used to trigger change detection after async user updates. */
    private cdr: ChangeDetectorRef,
    /** Used to show a toast message when the user is not logged in. */
    private snackBar: MatSnackBar,
  ) {}

  /**
   * Subscribes to user updates so the banner disappears once the
   * agreement is signed. Fetches the current user if not already loaded.
   * @returns {void}
   */
  ngOnInit(): void {
    this.sub = this.userService.userUpdated$.subscribe(() => {
      this.cdr.markForCheck();
    });

    if (!this.userService.currentUser) {
      this.userService.getCurrentUser().subscribe();
    }
  }

  /**
   * Cleans up the user update subscription.
   * @returns {void}
   */
  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /**
   * Whether the banner should currently be shown.
   * @returns {boolean}
   */
  get show(): boolean {
    return this.alertsService.privacyAgreementPending;
  }

  /**
   * Navigates to the privacy agreement page if logged in.
   * Shows a snackbar message and redirects to login if not authenticated.
   * @returns {void}
   */
  review(): void {
    if (!this.userService.currentUser) {
      this.snackBar
        .open('Please log in to review the privacy agreement.', 'Log in', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        })
        .onAction()
        .subscribe(() => {
          this.router.navigate(['/login']);
        });
      return;
    }
    this.router.navigate(['/privacy']);
  }
}
